/*
 *
 * EnvAuthenticator action/reducer
 *
 */

import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { resetState } from "scenes/DashboardMfa/redux";
import { mfaFlow } from "utils/enum";
import { createActionDispatchers } from "utils/reduxHelpers";
import {
  EnvAuthenticatorStatusEnum,
  PassCodeSubmissionStatusEnum,
} from "./enums";
import { SubmissionStateType } from "./types";
import { goToSecuritySettings, getTnCForCidpUser, setUserAcceptedNewTnC } from "scenes/redux";

export type State = {
  loadingQRLink: boolean;
  loadingPasscodeSubmission: SubmissionStateType;
  loadingStatus: boolean;

  showError: boolean;

  qrCodeImg: any;
  secretCode: string;
  status: EnvAuthenticatorStatusEnum;
  currentFlow: mfaFlow.SECURITY;
};

export const initialState: State = {
  loadingQRLink: false,
  loadingPasscodeSubmission: PassCodeSubmissionStatusEnum.INITIATE,
  loadingStatus: false,

  showError: false,

  qrCodeImg: undefined,
  secretCode: "",
  currentFlow: mfaFlow.SECURITY,
  status: EnvAuthenticatorStatusEnum.INITIAL,
};

const slice = createSlice({
  name: "envAuthenticator",
  initialState,
  // create actions with reducer (slice action) here
  reducers: {
    loadQrCode(state: State, action: PayloadAction<any>) {
      state.loadingQRLink = true;
      state.currentFlow = action.payload.currentLayout;
    },
    loadStatus(state: State) {
      state.loadingStatus = false;
    },
    setError(state: State, action: PayloadAction<boolean>) {
      state.showError = action.payload;
    },
    setStatus(state: State, action: PayloadAction<EnvAuthenticatorStatusEnum>) {
      state.status = action.payload;
    },
    reset(state: State) {
      state.status = EnvAuthenticatorStatusEnum.INITIAL;
      state.loadingPasscodeSubmission = PassCodeSubmissionStatusEnum.INITIATE;
      state.showError = false;
      state.secretCode = "";
      state.qrCodeImg = undefined;
    },
    resetSubmissionStatus(state: State) {
      state.loadingPasscodeSubmission = PassCodeSubmissionStatusEnum.INITIATE;
      state.showError = false;
      state.status = EnvAuthenticatorStatusEnum.INITIAL;
    },
    afterEnvAuthenticatorFetch(
      state: State,
      action: PayloadAction<{ qrCodeImg: any; secretCode: string }>
    ) {
      state.loadingQRLink = false;
      state.qrCodeImg = action.payload.qrCodeImg;
      state.secretCode = action.payload.secretCode;
    },
    submitPasscode(state: State) {
      state.loadingPasscodeSubmission = PassCodeSubmissionStatusEnum.INITIATE;
    },
    afterPassCodeSubmission(
      state: State,
      action: PayloadAction<SubmissionStateType>
    ) {
      state.loadingPasscodeSubmission = action.payload;
    },
    afterStatusFetch(
      state: State,
      action: PayloadAction<EnvAuthenticatorStatusEnum>
    ) {
      state.status = action.payload;
    },
  },
  // reducer for external action
  // extraReducers: (builder) => {
  //   builder.addCase(externalAction, (state) => {
  //   });
  // },
});

/**
 * create actions WITHOUT reducer (pure action) here
 */
export const pureAction = createAction("envAuthenticator/pureAction");

/**
 * actions from slice to be exported for saga
 */
export const {
  afterEnvAuthenticatorFetch,
  loadQrCode,
  submitPasscode,
  setError,
  afterPassCodeSubmission,
  afterStatusFetch,
  loadStatus,
  setStatus,
  reset,
  resetSubmissionStatus,
} = slice.actions;

/**
 * all actions for layout
 */
export const actionProps = createActionDispatchers(
  slice.actions,
  // slice actions
  [
    "loadQrCode",
    "submitPasscode",
    "loadStatus",
    "reset",
    "resetSubmissionStatus",
  ],
  // pure actions
  {
    resetState,
    goToMfaScreen: (redirect) => push(redirect),
    goToSecuritySettings,
    getTnCForCidpUser,
    setUserAcceptedNewTnC,
  }
);

export default slice.reducer;
